const environment = process.env

const parseEnvBool = (value) => String(value ?? '').trim().toLowerCase() === 'true'

const appEnv = environment.VITE_APP_ENV

export const app = {
    env: appEnv,
    isProd: appEnv === 'production',
    isStaging: appEnv === 'staging',
    isDev: appEnv === 'development',
    isWorkstation: appEnv === 'workstation',
    debug: environment.VITE_APP_DEBUG,
    hotReload: environment.VITE_APP_HOT_RELOAD,
    gaMeasurementId: environment.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID,
    gaGoogleAdsAccountId: environment.VITE_GOOGLE_ADWORDS_ACCOUNT_ID,
    gaDebug: environment.VITE_GA_DEBUG,
    buyNowPayLaterLink: environment.VITE_BNPL_LINK,
    pointsValueCAD: parseFloat(environment.VITE_POINTS_VALUE_CAD || 0.01),
    supportedPlatforms: environment.VITE_SUPPORTED_PLATFORMS,
    stripePublishableKey: environment.VITE_STRIPE_PUBLISHABLE_KEY,
    redditPixelId: environment.VITE_REDDIT_PIXEL_ID,
    metaPixelId: environment.VITE_META_PIXEL_ID,
}

/**
 * Which shell the same build is running in. The web app and the Capacitor
 * mobile app ship from one codebase, so mobile-only behaviour is gated on this
 * rather than on user-agent sniffing (which cannot distinguish the app's
 * WebView from a phone browser reliably).
 *
 * Set at build time — `VITE_APP_PLATFORM=mobile` for app builds. Real shell
 * variables take priority over every `.env*` file (dotenv does not override an
 * existing `process.env` value), so no per-mode env file is needed: a mobile
 * build is `VITE_APP_PLATFORM=mobile` on top of any normal mode.
 *
 * Defaults to `web`, so lodgr.ca is unaffected by anything gated on this.
 */
const appPlatform = environment.VITE_APP_PLATFORM || 'web'

export const platform = {
    name: appPlatform,
    isMobileApp: appPlatform === 'mobile',
    isWeb: appPlatform !== 'mobile',
    // Host Core Auth returns a mobile login to (`https://<host>/mobile-oauth-callback`).
    // Production has its own Core client returning to lodgr.ca; every other
    // API lane uses the dev client, which returns to dev.lodgr.co regardless
    // of the lane's own origin. iOS needs it up front for the login sheet.
    oauthCallbackHost: environment.VITE_MOBILE_OAUTH_CALLBACK_HOST || 'dev.lodgr.co',
}

export const api = {
    base: environment.VITE_APP_API_BASE,
    mode: environment.VITE_APP_API_MODE || environment.VITE_APP_ENV,
}

export const web = {
    base: environment.VITE_APP_WEB_BASE,
}

export const ext = {
    url: environment.VITE_APP_EXT_URL,
}

/*
    TENANT-5307, gateway experiment.

    The in-page gateway serves an Airbnb listing with the Lodgr extension
    already running in it, so a visitor can try the extension without
    installing it. It is a separate service and it is not deployed anywhere
    yet, so `base` is unset in every environment and the entry block on
    /browser-extension does not render. Point it at a running gateway to turn
    the experiment on:

        VITE_APP_GATEWAY_BASE=http://127.0.0.1:5601
*/
export const gateway = {
    base: environment.VITE_APP_GATEWAY_BASE,
}

export const features = {
    search: environment.VITE_FEATURES_SEARCH === 'true',
}

export const qaOverlay = {
    enabledFlag: environment.VITE_QA_OVERLAY_ENABLED,
    enabled: parseEnvBool(environment.VITE_QA_OVERLAY_ENABLED),
}

export const sentry = {
    dsn: environment.VITE_SENTRY_DSN,
    environment: environment.VITE_SENTRY_ENVIRONMENT_NAME,
}

export const posthog = {
    key: environment.VITE_APP_POSTHOG_KEY,
    host: environment.VITE_APP_POSTHOG_HOST,
    debug: environment.VITE_POSTHOG_DEBUG === 'true',
}

export const adwordConversionCodes = {
    signup: environment.VITE_GOOGLE_ADWORDS_ACCOUNT_ID + '/' + environment.VITE_GOOGLE_ADWORDS_CONVERSION_LABEL_SIGNUP,
    scheduleCall: environment.VITE_GOOGLE_ADWORDS_ACCOUNT_ID + '/' + environment.VITE_GOOGLE_ADWORDS_CONVERSION_LABEL_SCHEDULE_CALL,
    bookingRequest: environment.VITE_GOOGLE_ADWORDS_ACCOUNT_ID + '/' + environment.VITE_GOOGLE_ADWORDS_CONVERSION_LABEL_BOOKING_REQUEST,
    propertyActivation: environment.VITE_GOOGLE_ADWORDS_ACCOUNT_ID + '/' + environment.VITE_GOOGLE_ADWORDS_CONVERSION_LABEL_PROPERTY_ACTIVATION,
}

export const coreApi = {
    authUrl: environment.VITE_CORE_API_AUTH_URL,
}

export const googleMaps = {
    apiKey: environment.VITE_GOOGLE_MAPS_API_KEY,
}

export default {
    api,
    app,
    coreApi,
    features,
    qaOverlay,
    adwordConversionCodes,
    web,
    ext,
    gateway,
    sentry,
    posthog,
    googleMaps,
}
